import { Injectable } from '@angular/core';
import { AngularFirestore} from '@angular/fire/firestore';
import { AngularFireAuth } from '@angular/fire/auth';
import firebase from "firebase";
import {Account} from "../models/account.model";
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ZonesSlotActivity,
  ZonesSlotActivityFilter,
  ZonesSlotActivitySummary,
  SlotStatus,
  ZoneSlot,
  PaginatedZonesSlotActivities
} from '../models/audit.model';

@Injectable({
  providedIn: 'root'
})
export class ZonesSlotService {

  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore
  ) {

  }

  async updateAccountByZoneSlot(slot_count: any, accountKey, zone_id: any) {

    const accountRef = firebase.firestore().collection('accounts');
    const query = await accountRef
      .where('account_key', '==', accountKey)
      .limit(1)
      .get();

    if(query.empty){
      console.log('No data found');
    }else{
      query.docs.map((item) =>{
        let itemData = item.data();
        let slots = itemData.settings.zones;

        slots.forEach((slot, index) =>{
          if(slot.zone_id === zone_id){
            slots[index].zone_slots = slot_count;
            let data: any = { settings : {zones: slots} };
            item.ref.update(data);
          }
        });

      });
    }

  }

  updateZonesSlot(id: string, data: any): Promise<void> {
    return this.afs
      .collection("zones_slot")
      .doc(id)
      .update(data);
  }

  async getZoneSlotById(log_id: any, slot_id: any, accountKey: any) {
    return new Promise(async (resolve, reject) => {
      const logRef = firebase.firestore().collection('zones_slot');
      const query = await logRef
        .where('account_key', '==', accountKey)
        .where('slot_id', '==', slot_id)
        .where('log_id', '==', log_id)
        .limit(1)
        .get();

      if (query.empty) {
        resolve(false);
      } else {
        resolve(true);
      }
    });
  }

  async getSlotsByZoneId(zone_id: any, accountKey: any) {
    try {
      const logRef = firebase.firestore().collection('zones_slot');
      const query = await logRef
        .where('account_key', '==', accountKey)
        .where('zone_id', '==', zone_id)
        .get();

      if (query.empty) {
        return [];  // Return an empty array if no slots are found
      } else {
        // Extract and return all slots from the query result
        return query.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }
    } catch (error) {
      console.error("Error fetching slots: ", error);
      throw new Error("Error fetching slots");
    }
  }

  async getSlotsByZoneIdAndSlotId(zone_id: any, slot_id: any): Promise<ZoneSlot> {
    try {
      const logRef = firebase.firestore().collection('zones_slot');
      const query = await logRef
        .where('slot_id', '==', slot_id)
        .where('zone_id', '==', zone_id)
        .limit(1)
        .get();

      if (query.empty) {
        return null;  // Return null if no slot is found
      } else {
        // Return the first matching slot
        const doc = query.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        } as ZoneSlot;
      }
    } catch (error) {
      console.error("Error fetching slots: ", error);
      throw new Error("Error fetching slots");
    }
  }


  async updateZonesSlotBySlotNameAndZoneId(zoneId: any, slotName: any, data: any, accountKey: any) {
    return new Promise(async (resolve, reject) => {
      const logRef = firebase.firestore().collection('zones_slot');
      const query = await logRef
        .where('account_key', '==', accountKey)
        .where('zone_id', '==', zoneId)
        .where('slot_name', '==', slotName)
        .limit(1)
        .get();

      if (query.empty) {
        console.log('No data found');
        resolve(false)
      } else {
        query.docs.map((item) => {
          item.ref.update(data).then((updateResult) => {
            resolve(true)
          });
        });
      }

    });

  }

  async deleteSlots(zoneId: any, slotName: any, accountKey: any){
    const logRef = firebase.firestore().collection('zones_slot');
    const query = await logRef
      .where('account_key', '==', accountKey)
      .where('zone_id', '==', zoneId)
      .where('slot_name', '==', slotName)
      .get();

    if(query.empty){
      console.log('No data found');
    }else{
      query.docs.map((item) =>{
        logRef.doc(item.id).delete();
      });
    }
  }

  // Zone Slot Activity Methods
  async getZoneSlotActivities(filter: ZonesSlotActivityFilter): Promise<ZonesSlotActivity[]> {
    try {
      console.log('🔍 ZonesSlotService: Starting getZoneSlotActivities with filter:', filter);

      // First, let's try a simple query to see if there's any data at all
      console.log('🔍 Testing simple query on zones_slot collection...');
      const testQuery = firebase.firestore().collection('zones_slot_activity').limit(5);
      const testSnapshot = await testQuery.get();
      console.log('📋 Test query result - documents found:', testSnapshot.size);

      if (testSnapshot.size > 0) {
        console.log('📋 Sample document structure:', testSnapshot.docs[0].data());
      }

      let query: any = firebase.firestore().collection('zones_slot_activity');

      console.log('🔍 Base query created');

      // Add filters
      if (filter.account_key) {
        query = query.where('account_key', '==', filter.account_key);
        console.log('🔍 Added account_key filter:', filter.account_key);
      }

      if (filter.zone_id) {
        query = query.where('zone_id', '==', filter.zone_id);
        console.log('🔍 Added zone_id filter:', filter.zone_id);
      }

      if (filter.slot_id) {
        query = query.where('slot_id', '==', filter.slot_id);
        console.log('🔍 Added slot_id filter:', filter.slot_id);
      }

      if (filter.action_type) {
        query = query.where('action_type', '==', filter.action_type);
        console.log('🔍 Added action_type filter:', filter.action_type);
      }

      // Add date range filtering
      if (filter.start_date && filter.end_date) {
        const startTimestamp = firebase.firestore.Timestamp.fromDate(filter.start_date);
        const endTimestamp = firebase.firestore.Timestamp.fromDate(filter.end_date);
        query = query
          .where('timestamp', '>=', startTimestamp)
          .where('timestamp', '<=', endTimestamp);
        console.log('🔍 Added date range filter:', filter.start_date, 'to', filter.end_date);
      }

      // Order by most recent - but check if timestamp field exists
      try {
        query = query.orderBy('timestamp', 'desc');
        console.log('🔍 Added timestamp ordering');
      } catch (orderError) {
        console.warn('⚠️ Could not order by timestamp, proceeding without ordering:', orderError.message);
        // Remove the orderBy and try without it
        query = firebase.firestore().collection('zones_slot')
          .where('account_key', '==', filter.account_key);
      }

      // Apply limit
      if (filter.limit) {
        query = query.limit(filter.limit);
        console.log('🔍 Added limit:', filter.limit);
      }

      console.log('🔍 Executing Firebase query...');
      const snapshot = await query.get();

      console.log('📋 Query result - documents found:', snapshot.size);

      if (snapshot.empty) {
        console.log('📋 No documents found matching the query');

        // Let's try a query without the account_key filter to see if there's any data
        console.log('🔍 Testing query without account_key filter...');
        const noFilterQuery = firebase.firestore().collection('zones_slot').limit(10);
        const noFilterSnapshot = await noFilterQuery.get();
        console.log('📋 Documents without account_key filter:', noFilterSnapshot.size);

        if (noFilterSnapshot.size > 0) {
          console.log('📋 Available account_keys in collection:');
          noFilterSnapshot.docs.forEach((doc, index) => {
            const data = doc.data();
            console.log(`📋 Document ${index + 1} account_key:`, data.account_key);
          });
        }

        return [];
      }

      const activities: ZonesSlotActivity[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        const activity: ZonesSlotActivity = {
          id: doc.id,
          account_key: data.account_key,
          action_type: data.action_type,
          log_id: data.log_id,
          previous_log_id: data.previous_log_id,
          slot_id: data.slot_id,
          slot_name: data.slot_name,
          timestamp: data.timestamp,
          updated_by_id: data.updated_by_id,
          updated_by_name: data.updated_by_name,
          zone_id: data.zone_id,
          zone_name: data.zone_name
        } as ZonesSlotActivity;
        activities.push(activity);
      });

      console.log('📋 Total activities before client-side filtering:', activities.length);

      // Apply search term filter on client side
      let filteredActivities = activities;
      if (filter.search_term) {
        const searchTerm = filter.search_term.toLowerCase();
        console.log('🔍 Applying search filter:', searchTerm);
        filteredActivities = activities.filter(activity =>
          activity.slot_name?.toLowerCase().includes(searchTerm) ||
          activity.zone_name?.toLowerCase().includes(searchTerm) ||
          activity.log_id?.toLowerCase().includes(searchTerm) ||
          activity.updated_by_name?.toLowerCase().includes(searchTerm)
        );
        console.log('📋 Activities after search filter:', filteredActivities.length);
      }

      console.log('📋 Final activities returned:', filteredActivities.length);
      return filteredActivities;
    } catch (error) {
      console.error('Error fetching zone slot activities:', error);
      throw error;
    }
  }

  /**
   * Get zone slot activities with cursor-based pagination (efficient for large datasets)
   * Optimized for performance with proper error handling
   */
  async getZoneSlotActivitiesPaginated(filter: ZonesSlotActivityFilter): Promise<PaginatedZonesSlotActivities> {
    const startTime = performance.now();
    const pageSize = filter?.limit || 10;

    try {
      const db = firebase.firestore();
      let query: firebase.firestore.Query = db.collection('zones_slot_activity');

      // Apply equality filters first (better index utilization)
      if (filter.account_key) {
        query = query.where('account_key', '==', filter.account_key);
      }

      if (filter.zone_id) {
        query = query.where('zone_id', '==', filter.zone_id);
      }

      if (filter.slot_id) {
        query = query.where('slot_id', '==', filter.slot_id);
      }

      if (filter.action_type !== undefined) {
        query = query.where('action_type', '==', filter.action_type);
      }

      // Range filters (must come after equality filters)
      if (filter.start_date) {
        query = query.where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(filter.start_date));
      }

      if (filter.end_date) {
        query = query.where('timestamp', '<=', firebase.firestore.Timestamp.fromDate(filter.end_date));
      }

      // Order by timestamp descending (required for cursor pagination)
      query = query.orderBy('timestamp', 'desc');

      // Apply cursor for pagination
      if (filter?.lastDocId) {
        try {
          const lastDocSnapshot = await db.collection('zones_slot_activity').doc(filter.lastDocId).get();
          if (lastDocSnapshot.exists) {
            query = query.startAfter(lastDocSnapshot);
          }
        } catch (cursorError) {
          console.warn('⚠️ Could not fetch cursor document, starting from beginning:', cursorError);
        }
      }

      // Fetch one extra to determine if there are more results
      query = query.limit(pageSize + 1);

      // Execute query
      const snapshot = await query.get();
      const docs = snapshot.docs;

      // Determine pagination state
      const hasMore = docs.length > pageSize;
      const resultDocs = hasMore ? docs.slice(0, pageSize) : docs;

      // Map documents efficiently using spread operator
      const activities: ZonesSlotActivity[] = resultDocs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ZonesSlotActivity));

      const lastDocId = resultDocs.length > 0 ? resultDocs[resultDocs.length - 1].id : null;
      const queryTime = Math.round(performance.now() - startTime);

      console.log(`📋 Slot activities query completed in ${queryTime}ms: ${activities.length} activities, hasMore: ${hasMore}`);

      return {
        activities,
        lastDocId,
        hasMore,
        totalFetched: activities.length
      };
    } catch (error) {
      const queryTime = Math.round(performance.now() - startTime);
      console.error(`❌ Slot activities query failed after ${queryTime}ms:`, error?.message || error);

      // Return empty result on error (graceful degradation)
      return {
        activities: [],
        lastDocId: null,
        hasMore: false,
        totalFetched: 0
      };
    }
  }

  async getZonesSlotActivitySummary(filter: ZonesSlotActivityFilter): Promise<ZonesSlotActivitySummary> {
    try {
      const activities = await this.getZoneSlotActivities(filter);

      const summary: ZonesSlotActivitySummary = {
        total_activities: activities.length,
        unique_zones: 0,
        unique_slots: 0,
        action_types: [],
        zone_breakdown: []
      };

      // Count unique zones, slots, and action types
      const uniqueZones = new Set<string>();
      const uniqueSlots = new Set<string>();
      const actionTypeCounts = new Map<number, number>();
      const zoneBreakdown = new Map<string, any>();

      activities.forEach(activity => {
        // Count unique zones and slots
        if (activity.zone_id) uniqueZones.add(activity.zone_id);
        if (activity.slot_id) uniqueSlots.add(activity.slot_id);

        // Count action types
        if (activity.action_type !== undefined) {
          actionTypeCounts.set(activity.action_type, (actionTypeCounts.get(activity.action_type) || 0) + 1);
        }

        // Zone breakdown
        const zoneId = activity.zone_id || 'unknown';
        if (!zoneBreakdown.has(zoneId)) {
          zoneBreakdown.set(zoneId, {
            zone_id: zoneId,
            zone_name: activity.zone_name || zoneId,
            activity_count: 0,
            last_activity: activity.timestamp
          });
        }

        const zoneData = zoneBreakdown.get(zoneId);
        zoneData.activity_count++;

        // Update last activity if this one is more recent
        if (activity.timestamp && (!zoneData.last_activity || activity.timestamp.seconds > zoneData.last_activity.seconds)) {
          zoneData.last_activity = activity.timestamp;
        }
      });

      summary.unique_zones = uniqueZones.size;
      summary.unique_slots = uniqueSlots.size;
      summary.action_types = Array.from(actionTypeCounts.entries()).map(([action_type, count]) => ({ action_type, count }));
      summary.zone_breakdown = Array.from(zoneBreakdown.values());

      return summary;
    } catch (error) {
      console.error('Error getting zone slot activity summary:', error);
      throw error;
    }
  }

  // Observable method for real-time updates
  getZoneSlotActivitiesObservable(filter: ZonesSlotActivityFilter): Observable<ZonesSlotActivity[]> {
    return this.afs.collection('zones_slot_activity', ref => {
      let query: any = ref;

      if (filter.account_key) {
        query = query.where('account_key', '==', filter.account_key);
      }

      if (filter.zone_id) {
        query = query.where('zone_id', '==', filter.zone_id);
      }

      if (filter.slot_id) {
        query = query.where('slot_id', '==', filter.slot_id);
      }

      if (filter.action_type) {
        query = query.where('action_type', '==', filter.action_type);
      }

      if (filter.start_date && filter.end_date) {
        const startTimestamp = firebase.firestore.Timestamp.fromDate(filter.start_date);
        const endTimestamp = firebase.firestore.Timestamp.fromDate(filter.end_date);
        query = query
          .where('timestamp', '>=', startTimestamp)
          .where('timestamp', '<=', endTimestamp);
      }

      query = query.orderBy('timestamp', 'desc');

      if (filter.limit) {
        query = query.limit(filter.limit);
      }

      return query;
    }).snapshotChanges()
      .pipe(
        map(actions => {
          let activities = actions.map(action => {
            const data = action.payload.doc.data() as any;
            const id = action.payload.doc.id;
            return {
              id,
              account_key: data.account_key,
              action_type: data.action_type,
              log_id: data.log_id,
              previous_log_id: data.previous_log_id,
              slot_id: data.slot_id,
              slot_name: data.slot_name,
              timestamp: data.timestamp,
              updated_by_id: data.updated_by_id,
              updated_by_name: data.updated_by_name,
              zone_id: data.zone_id,
              zone_name: data.zone_name
            } as ZonesSlotActivity;
          });

          // Apply search term filter
          if (filter.search_term) {
            const searchTerm = filter.search_term.toLowerCase();
            activities = activities.filter(activity =>
              activity.slot_name?.toLowerCase().includes(searchTerm) ||
              activity.zone_name?.toLowerCase().includes(searchTerm) ||
              activity.log_id?.toLowerCase().includes(searchTerm) ||
              activity.updated_by_name?.toLowerCase().includes(searchTerm)
            );
          }

          return activities;
        })
      );
  }


}
